import cn from 'classnames';
import { CommentAnchor } from '../comments-constants';
import { CommentThread } from './CommentThread';
import { AuditReport, ScanSource, ScanSourceType } from '../digital-audit-constants';
import { DiscoverabilityInfo, PageMetric } from '../engine/martech-scanner-constants';
import { PageRenderCheck, RenderSummary, RenderVerdict } from '../engine/site-health-utils';
import { ObservationCard } from './ObservationCard';
import { ScanSourceBadge } from './ScanSourceBadge';

interface SiteHealthViewProps {
  report: AuditReport | null;
}

// Server-response coloring; thresholds documented on the Glossary tab.
const speedClass = (ms: number): string => {
  if (ms < 400) return 'good';
  if (ms <= 1000) return 'warn';
  return 'miss';
};

const formatKb = (bytes: number): string => `${Math.round(bytes / 1024)} KB`;

const pagePath = (url: string): string => {
  try {
    const u = new URL(url);
    return u.pathname === '/' ? u.hostname : u.pathname;
  } catch {
    return url;
  }
};

// A single monolithic sitemap at this size is where splitting starts paying
// off in indexation rate — the observation the SEO team asked for.
const SPLIT_SITEMAP_URL_THRESHOLD = 2000;

const RENDER_VERDICT_LABEL: Record<string, { label: string; cls: string }> = {
  [RenderVerdict.SERVER_RENDERED]: { label: 'renders without JS', cls: 'good' },
  [RenderVerdict.PARTIAL]: { label: 'partial content without JS', cls: 'warn' },
  [RenderVerdict.JS_REQUIRED]: { label: 'JS required for content', cls: 'miss' }
};

interface PageMetricsSectionProps {
  metrics: PageMetric[];
}

const PageMetricsSection = ({ metrics }: PageMetricsSectionProps) => (
  <div className='digital-audit-section'>
    <div className='digital-audit-section-head'>
      <div className='digital-audit-section-title'>Load speed</div>
      <ScanSourceBadge scanSource={ScanSource.QUICK} />
      <div className='digital-audit-section-count'>
        server response measured from the scanner — not Core Web Vitals (see Glossary)
      </div>
    </div>
    <table className='digital-audit-table'>
      <thead>
        <tr>
          <th>Page</th>
          <th>Status</th>
          <th>Server response</th>
          <th>HTML download</th>
          <th>Document size</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map(m => (
          <tr key={m.url}>
            <td className='digital-audit-table-mono'>{pagePath(m.url)}</td>
            <td className='digital-audit-table-mono'>{m.status}</td>
            <td>
              <span className={cn('digital-audit-chbadge', speedClass(m.headerMs))}>
                {m.headerMs} ms
              </span>
            </td>
            <td className='digital-audit-table-mono'>{m.totalMs} ms</td>
            <td className='digital-audit-table-mono'>{formatKb(m.bytes)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <CommentThread anchor={CommentAnchor.LOAD_SPEED} />
  </div>
);

interface FactRowProps {
  ok: boolean | null;
  label: string;
  detail: string;
}

const factMark = (ok: boolean | null): string => {
  if (ok === null) return '•';
  return ok ? '✓' : '✗';
};

const factClass = (ok: boolean | null): string => {
  if (ok === null) return 'info';
  return ok ? 'good' : 'miss';
};

const FactRow = ({ ok, label, detail }: FactRowProps) => {
  const mark = factMark(ok);
  const cls = factClass(ok);
  return (
    <div className={cn('digital-audit-chk', cls)}>
      <span className='digital-audit-chk-mk'>{mark}</span>
      <span>
        <span className='digital-audit-chk-label'>{label}</span>{' '}
        <span className='digital-audit-chk-detail'>{detail}</span>
      </span>
    </div>
  );
};

interface DiscoverabilitySectionProps {
  disc: DiscoverabilityInfo;
}

const sitemapDetail = (sitemap: DiscoverabilityInfo['sitemap']): string => {
  if (!sitemap.exists) {
    return 'neither /sitemap.xml nor a robots.txt Sitemap: declaration answered';
  }
  if (sitemap.isIndex) {
    return `sitemap index with ${sitemap.childSitemaps} child sitemap(s) · ~${sitemap.sampledUrlCount} URLs sampled from the first child`;
  }
  return `single sitemap with ${sitemap.sampledUrlCount} URL(s)`;
};

const DiscoverabilitySection = ({ disc }: DiscoverabilitySectionProps) => {
  const { robots, sitemap, llmsTxt } = disc;
  const singleLargeSitemap =
    sitemap.exists && !sitemap.isIndex && sitemap.sampledUrlCount >= SPLIT_SITEMAP_URL_THRESHOLD;
  const allowedBots = robots.aiBots.filter(b => b.allowed);

  return (
    <div className='digital-audit-section'>
      <div className='digital-audit-section-head'>
        <div className='digital-audit-section-title'>Discoverability</div>
        <ScanSourceBadge scanSource={ScanSource.QUICK} />
        <div className='digital-audit-section-count'>
          crawling &amp; indexing standards — sitemap, robots, AI access
        </div>
      </div>

      <div>
        <span className='digital-audit-obs-group-label'>Sitemap &amp; indexation</span>
        <FactRow
          ok={sitemap.exists}
          label={sitemap.exists ? 'Sitemap found' : 'No sitemap found'}
          detail={sitemapDetail(sitemap)}
        />
        {sitemap.exists && <FactRow ok={null} label='Managed by' detail={sitemap.managerHint} />}
        <FactRow
          ok={robots.sitemaps.length > 0}
          label={
            robots.sitemaps.length > 0
              ? 'Sitemap declared in robots.txt'
              : 'Sitemap not declared in robots.txt'
          }
          detail={
            robots.sitemaps.length > 0
              ? robots.sitemaps.join(' · ')
              : 'declaring it helps crawlers find every sitemap without guessing'
          }
        />
      </div>

      <div>
        <span className='digital-audit-obs-group-label'>Crawler &amp; AI access</span>
        <FactRow
          ok={robots.exists}
          label={robots.exists ? 'robots.txt present' : 'No robots.txt'}
          detail={robots.exists ? '' : 'crawlers fall back to crawling everything, uncontrolled'}
        />
        {robots.exists && (
          <FactRow
            ok={!robots.googlebotBlocked}
            label={robots.googlebotBlocked ? 'Googlebot blocked site-wide' : 'Googlebot allowed'}
            detail={robots.googlebotBlocked ? 'the site is invisible to Google Search' : ''}
          />
        )}
        {robots.exists && robots.aiBots.length > 0 && (
          <FactRow
            ok={null}
            label={`AI/LLM crawlers: ${allowedBots.length} of ${robots.aiBots.length} allowed`}
            detail={robots.aiBots.map(b => `${b.allowed ? '✓' : '✗'} ${b.bot}`).join(' · ')}
          />
        )}
        <FactRow
          ok={llmsTxt}
          label={llmsTxt ? 'llms.txt published' : 'No llms.txt'}
          detail={
            llmsTxt
              ? 'the site curates content for AI agents'
              : 'an emerging standard for steering AI assistants to canonical content'
          }
        />
      </div>

      {(singleLargeSitemap || robots.googlebotBlocked || !sitemap.exists) && (
        <div className='digital-audit-obs-list'>
          {singleLargeSitemap && (
            <ObservationCard
              severity='OPPORTUNITY'
              title={`Single sitemap with ~${sitemap.sampledUrlCount} URLs — consider splitting into an index`}
              note='Large single sitemaps slow discovery and hide indexation problems. Splitting into a sitemap index (by section or content type) lets Search Console report indexation rate per sitemap, making gaps visible and fixable.'
            />
          )}
          {!sitemap.exists && (
            <ObservationCard
              severity='RISK'
              title='No sitemap found'
              note='Without a sitemap, discovery depends entirely on crawl paths — new and deep pages index slowly or not at all. Generating one (platform plugin or manual) is the first discoverability fix.'
            />
          )}
          {robots.googlebotBlocked && (
            <ObservationCard
              severity='RISK'
              title='robots.txt blocks Googlebot from the whole site'
              note='Unless this is a staging environment, this removes the site from Google Search. Confirm immediately.'
            />
          )}
        </div>
      )}
      <CommentThread anchor={CommentAnchor.DISCOVERABILITY} />
    </div>
  );
};

interface RenderSectionProps {
  summary: RenderSummary;
  scanSource: ScanSourceType;
}

const RenderSection = ({ summary, scanSource }: RenderSectionProps) => (
  <div className='digital-audit-section'>
    <div className='digital-audit-section-head'>
      <div className='digital-audit-section-title'>JavaScript rendering</div>
      <ScanSourceBadge scanSource={scanSource} />
      <div className='digital-audit-section-count'>
        {summary.jsRequiredPct}% of scanned pages appear to need JS for primary content — heuristic
        from initial HTML (see Glossary)
      </div>
    </div>
    <table className='digital-audit-table'>
      <thead>
        <tr>
          <th>Page</th>
          <th>Text in initial HTML</th>
          <th>Links in initial HTML</th>
          <th>Verdict</th>
        </tr>
      </thead>
      <tbody>
        {summary.checks.map((c: PageRenderCheck) => {
          const v = RENDER_VERDICT_LABEL[c.verdict];
          return (
            <tr key={c.url}>
              <td className='digital-audit-table-mono'>{pagePath(c.url)}</td>
              <td className='digital-audit-table-mono'>{c.textChars.toLocaleString()} chars</td>
              <td className='digital-audit-table-mono'>{c.linkCount}</td>
              <td>
                <span className={cn('digital-audit-chbadge', v.cls)}>{v.label}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <CommentThread anchor={CommentAnchor.JS_RENDERING} />
  </div>
);

export const SiteHealthView = ({ report }: SiteHealthViewProps) => {
  if (!report) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>Run a scan to see site health.</div>
      </div>
    );
  }

  // Load speed/discoverability always come from the quick-scan snapshot
  // (see AuditReport.quickScanSiteHealth) - a full-scan merge never touches
  // it, so these sections stay populated instead of going blank once a full
  // scan lands. renderSummary comes from the live scanResult, so it's
  // recomputed from the full scan's real evidence once merged - the
  // ScanSourceBadge on each section tells the two apart at a glance.
  const { pageMetrics, discoverability } = report.quickScanSiteHealth;
  const { renderSummary } = report.scanResult;
  const hasPageMetrics = Boolean(pageMetrics?.length);
  const hasDiscoverability = Boolean(discoverability);
  const hasRenderSummary = Boolean(renderSummary?.checks.length);
  if (!hasPageMetrics && !hasDiscoverability && !hasRenderSummary) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>
          This scan predates the site-health fields — run a fresh scan to populate load speed,
          discoverability, and JavaScript-rendering checks.
        </div>
      </div>
    );
  }

  return (
    <div className='digital-audit-page'>
      {pageMetrics && pageMetrics.length > 0 && <PageMetricsSection metrics={pageMetrics} />}
      {discoverability && <DiscoverabilitySection disc={discoverability} />}
      {renderSummary && renderSummary.checks.length > 0 && (
        <RenderSection summary={renderSummary} scanSource={report.scanSource} />
      )}
    </div>
  );
};
